/**
 * Hook pour récupérer les informations des participants du chat.
 * Gère les noms d'affichage et avatars des utilisateurs et employés.
 */
import { useState, useEffect } from 'react';
import { ChatRoom } from '../services/chat';
import { api } from '../services/api';
import { useAuthStore } from '../stores/authStore';

export interface ParticipantInfo {
  id: number;
  type: 'user' | 'employee' | 'client';
  name: string;
  email: string;
  avatar_url?: string;
}

export const useChatParticipants = (room: ChatRoom | null) => {
  const [participants, setParticipants] = useState<Map<string, ParticipantInfo>>(new Map());
  const [loading, setLoading] = useState(false);
  const { user: currentUser } = useAuthStore();

  useEffect(() => {
    const loadParticipants = async () => {
      if (!room?.participants) return;

      setLoading(true);
      const participantMap = new Map<string, ParticipantInfo>();

      try {
        // Collect IDs
        const userIds = room.participants
          .filter(p => p.user_id)
          .map(p => p.user_id!);

        const employeeIds = room.participants
          .filter(p => p.employee_id)
          .map(p => p.employee_id!);

        const buyerIds = room.participants
          .filter(p => p.client_id)
          .map(p => p.client_id!);

        // Users: try loading individual profiles if endpoint accessible, else fallback
        for (const uid of userIds) {
          try {
            const res = await api.get(`/users/${uid}`);
            const u = res.data;
            participantMap.set(`user_${uid}`, {
              id: uid,
              type: 'user',
              name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || `Utilisateur ${uid}`,
              email: u.email || '',
              avatar_url: u.avatar_url || undefined,
            });
          } catch (_e) {
            if (!participantMap.has(`user_${uid}`)) {
              participantMap.set(`user_${uid}`, {
                id: uid,
                type: 'user',
                name: `Utilisateur ${uid}`,
                email: '',
                avatar_url: undefined,
              });
            }
          }
        }

        // Employees: load details for admins/super_admins/apporteurs, otherwise placeholders
        if (employeeIds.length > 0 && (currentUser?.role === 'admin' || currentUser?.role === 'super_admin' || currentUser?.role === 'apporteur')) {
          // Charger les employés un par un (pas d'endpoint batch disponible)
          for (const employeeId of employeeIds) {
            try {
              const employeeResponse = await api.get(`/employees/${employeeId}`);
              const employee = employeeResponse.data;
              
              participantMap.set(`employee_${employee.id}`, {
                id: employee.id,
                type: 'employee',
                name: `${employee.first_name} ${employee.last_name}`,
                email: employee.email,
                avatar_url: employee.profile_picture
              });
            } catch (error) {
              console.warn(`Failed to load employee ${employeeId}:`, error);
            }
          }
        } else if (employeeIds.length > 0) {
          // Pour les employés, utiliser des noms génériques
          employeeIds.forEach(employeeId => {
            participantMap.set(`employee_${employeeId}`, {
              id: employeeId,
              type: 'employee',
              name: `Employé ${employeeId}`,
              email: '',
              avatar_url: undefined
            });
          });
        }

        // Buyers: load details if endpoint accessible, else placeholders
        for (const bid of buyerIds) {
          try {
            const res = await api.get(`/clients/${bid}`);
            const b = res.data;
            participantMap.set(`buyer_${bid}`, {
              id: bid,
              type: 'client',
              name: b.company_name || `${b.first_name ?? ''} ${b.last_name ?? ''}`.trim() || b.email || `Client ${bid}`,
              email: b.email || '',
              avatar_url: undefined
            });
          } catch (_e) {
            participantMap.set(`buyer_${bid}`, {
              id: bid,
              type: 'client',
              name: `Client ${bid}`,
              email: '',
              avatar_url: undefined
            });
          }
        }

        setParticipants(participantMap);
      } catch (error) {
        console.error('Error loading participants:', error);
      } finally {
        setLoading(false);
      }
    };

    loadParticipants();
  }, [room?.id, room?.participants]);

  const getParticipantName = (userId?: number, employeeId?: number, clientId?: number): string => {
    if (userId) {
      const participant = participants.get(`user_${userId}`);
      return participant?.name || `Utilisateur ${userId}`;
    }
    if (employeeId) {
      const participant = participants.get(`employee_${employeeId}`);
      return participant?.name || `Employé ${employeeId}`;
    }
    if (clientId) {
      const participant = participants.get(`buyer_${clientId}`);
      return participant?.name || `Client ${clientId}`;
    }
    return 'Utilisateur inconnu';
  };

  const getParticipantAvatar = (userId?: number, employeeId?: number, clientId?: number): string | undefined => {
    if (userId) {
      return participants.get(`user_${userId}`)?.avatar_url;
    }
    if (employeeId) {
      return participants.get(`employee_${employeeId}`)?.avatar_url;
    }
    if (clientId) {
      return participants.get(`buyer_${clientId}`)?.avatar_url;
    }
    return undefined;
  };

  return {
    participants,
    loading,
    getParticipantName,
    getParticipantAvatar
  };
};
